import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Joyride, { type CallBackProps } from 'react-joyride';
import { useTourState } from '../hooks/useTourState';
import { usePushNudgeDialog } from '../components/push/PushNudgeDialog';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Heart, Share2, Settings, Pencil, Layers, ExternalLink, Loader2, ArrowUp, QrCode } from 'lucide-react';
import { gymsService } from '../services/gyms';
import { climbsService } from '../services/climbs';
import { climbScansService } from '../services/climb-scans';
import { devicesService } from '../services/devices';
import { gymVisitsService } from '../services/gymVisits';
import { useAuth } from '../hooks/useAuth';
import ClimbsList from '../components/climbs/ClimbsList';
import LeaderboardPreview from '../components/leaderboard/LeaderboardPreview';
import AnnouncementsCarousel from '../components/announcements/AnnouncementsCarousel';
import AnnouncementFormDialog from '../components/announcements/AnnouncementFormDialog';
import GymStaffActions from '../components/gym/GymStaffActions';
import FollowersList from '../components/FollowersList';
import LocationHeader from '../components/location/LocationHeader';
import { CollapsibleHeader } from '../components/location/CollapsibleHeader';
import { usePullToReveal } from '../hooks/usePullToReveal';
import OpeningStatusBadge from '../components/location/OpeningStatusBadge';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { trackEvent } from '../utils/analytics';
import type { Gym } from '../types/gym';
import type { Climb } from '../types/climb';
import type { Board } from '../types/device';
import type { GalleryImage } from '../types/location-images';
import GymStatsCard from '../components/gym/GymStatsCard';

// Shared styling for icon buttons overlaid on the cover image
const COVER_OVERLAY_BUTTON = 'h-9 w-9 bg-black/40 border-white/20 text-white hover:bg-black/60 hover:text-white';

export default function GymProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [gym, setGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);
  const [staffFilteredClimbs, setStaffFilteredClimbs] = useState<Climb[]>([]);
  const [statsGroupBy, setStatsGroupBy] = useState<'setter' | 'grade' | 'zone'>('setter');
  const [activeScanId, setActiveScanId] = useState<string | null>(null);
  // The gym's boards, shown as extra tabs. The endpoint decides which ones this
  // user sees: the boards a device drives need the :boards feature flag (earned
  // by authorizing at that device), the rest are listed for everybody.
  const [boards, setBoards] = useState<Board[]>([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [announcementReloadToken, setAnnouncementReloadToken] = useState(0);
  // What "new" is measured against on this visit — the browser's last look at
  // this wall on an earlier day — settled once per opening of the page, so the
  // "New" tags do not shift while it is open. Recorded before the climbs are
  // even asked for; see gymVisitsService for why it holds for the whole day.
  const [newSince, setNewSince] = useState<Date | null | undefined>(undefined);
  useEffect(() => {
    if (slug) setNewSince(gymVisitsService.recordVisit(slug));
  }, [slug]);
  // Track when the gym logo scrolls out of view (covered by the sticky navbar),
  // so the breadcrumb can swap the DivePeaks icon for the gym logo.
  const [logoCovered, setLogoCovered] = useState(false);
  const logoRef = useRef<HTMLDivElement>(null);

  // Mobile-only: selecting a climb tab collapses everything above it (hero + staff/stats).
  // Desktop keeps the header always visible.
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [collapsed, setCollapsed] = useState(false);

  const { run: runTour, complete: completeTour } = useTourState(
    'tour_gym_profile_seen',
    !!user && !loading
  );
  const pushNudge = usePushNudgeDialog('follow_gym');

  const gymTourSteps = [
    {
      target: '[data-tour="follow-button"]',
      title: t('tour.gymProfile.followTitle'),
      content: t('tour.gymProfile.followContent'),
      disableBeacon: true,
    },
    {
      target: '[data-tour="gym-filter-button"]',
      title: t('tour.gymProfile.filterTitle'),
      content: t('tour.gymProfile.filterContent'),
      disableBeacon: true,
    },
    {
      target: '[data-tour="gym-add-climb-button"]',
      title: t('tour.gymProfile.addClimbTitle'),
      content: t('tour.gymProfile.addClimbContent'),
      disableBeacon: true,
    },
    {
      target: '[data-testid="climb-row"]',
      title: t('tour.gymProfile.longPressTitle'),
      content: t('tour.gymProfile.longPressContent'),
      disableBeacon: true,
    },
  ];

  const handleGymTourCallback = (data: CallBackProps) => {
    if (data.status === 'finished' || data.status === 'skipped') {
      completeTour();
    }
  };

  // Get current user staff info for this gym from user profile
  const currentUserStaff = user?.staff?.find(staff => staff.gym_slug === slug) || null;

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;
    const fetchGym = async () => {
      setLoading(true);
      let gymData: unknown = null;
      let gymError: unknown = null;

      // The climbs are the slow half of this page and need only the slug, so
      // their request leaves now, with the gym's own, instead of once the gym
      // has answered and ClimbsList has mounted: that component asks for the
      // very same list a moment later and is handed this answer.
      climbsService.prefetchClimbs(
        { location_slug: slug, include_sends: true, include_user_attempts: true },
        { cache: 'no-cache' }
      );

      try {
        // Fetch the boards alongside the gym so the board tabs are ready when
        // the page renders, rather than popping in after the climbs.
        const [gymResult, boardsResult] = await Promise.allSettled([
          gymsService.getGymBySlug(slug),
          devicesService.listGymBoards(slug),
        ]);
        if (gymResult.status === 'fulfilled') {
          gymData = gymResult.value;
          trackEvent('gym_viewed', { gym_slug: slug });
        } else {
          gymError = gymResult.reason || new Error('Failed to fetch gym');
        }
        if (isMounted) {
          setBoards(boardsResult.status === 'fulfilled' ? boardsResult.value : []);
        }
      } catch (err) {
        gymError = err;
      }
      if (isMounted) {
        setGym(gymData as Gym | null);
        if (gymData && typeof gymData === 'object' && 'is_following' in gymData) {
          setIsFollowing((gymData as Gym).is_following || false);
        }
        setLoading(false);
        if (gymError) {
          console.error('Failed to fetch gym profile:', gymError);
          toast({
            title: t('gymProfile.failedToLoad'),
            description: gymError instanceof Error ? gymError.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        }
      }
    };

    fetchGym();
    return () => {
      isMounted = false;
    };
  }, [slug, t, toast]);

  const followGym = async () => {
    if (!user || !slug) {
      toast({
        title: t('gymProfile.loginRequired'),
        description: t('gymProfile.loginRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }

    setFollowLoading(true);
    try {
      await gymsService.followGym(slug);
      setIsFollowing(true);
      toast({
        title: t('gymProfile.followSuccess'),
        description: t('gymProfile.followSuccessDescription', { name: gym?.name }),
      });
      // The tour points at this very button: no ask on top of it.
      if (!runTour) pushNudge.maybeOpen({ name: gym?.name });
    } catch (error) {
      console.error('Failed to follow gym:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const unfollowGym = async () => {
    if (!slug) return;

    setFollowLoading(true);
    try {
      await gymsService.unfollowGym(slug);
      setIsFollowing(false);
      toast({
        title: t('gymProfile.unfollowSuccess'),
        description: t('gymProfile.unfollowSuccessDescription', { name: gym?.name }),
      });
    } catch (error) {
      console.error('Failed to unfollow gym:', error);
    } finally {
      setFollowLoading(false);
      setShowUnfollowConfirm(false);
    }
  };

  // Heart toggle on the cover: follow directly, but confirm before unfollowing
  const handleHeartClick = () => {
    if (!user) {
      toast({
        title: t('gymProfile.loginRequired'),
        description: t('gymProfile.loginRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }
    if (isFollowing) {
      setShowUnfollowConfirm(true);
    } else {
      followGym();
    }
  };

  const handleGoToSettings = () => {
    navigate(`/gyms/${slug}/settings`);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: gym?.name || t('gymProfile.share'),
          text: t('gymProfile.shareText', { name: gym?.name }),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: t('gymProfile.linkCopied'),
          description: t('gymProfile.linkCopiedDescription'),
          duration: 3000,
        });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
        toast({
          title: t('errors.shareFailed'),
          description: t('errors.shareFailedDescription'),
          variant: 'destructive',
        });
      }
    }
  };

  const isStaffAdmin = currentUserStaff?.staff_roles.includes('admin') && currentUserStaff?.status === 'confirmed';
  const isStaffMember = currentUserStaff?.status === 'confirmed' &&
    (currentUserStaff?.staff_roles.includes('admin') || currentUserStaff?.staff_roles.includes('editor'));

  // Fetch active scan ID for staff members to show "Completa caricamento" button
  useEffect(() => {
    if (!isStaffMember || !slug) return;
    climbScansService.getScans(slug)
      .then(scans => {
        const active = scans.find(s => s.status === 'pending' || s.status === 'processing' || s.status === 'ready');
        setActiveScanId(active?.id ?? null);
      })
      .catch(() => {});
  }, [isStaffMember, slug]);

  // Observe the gym logo: once it scrolls under the sticky navbar (h-16 = 64px),
  // surface it in the breadcrumb instead of the DivePeaks icon.
  useEffect(() => {
    const el = logoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setLogoCovered(!entry.isIntersecting),
      { rootMargin: '-64px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, gym]);

  // A new gym starts with its header expanded.
  useEffect(() => {
    setCollapsed(false);
  }, [slug]);

  // Reveal the collapsed header and return to the top (breadcrumb logo tap / gesture).
  const handleRevealHeader = useCallback(() => {
    setCollapsed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Collapse the header when a climb tab is selected (mobile only), focusing the climbs.
  const handleTabSelect = useCallback(() => {
    if (isDesktop) return;
    setCollapsed(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isDesktop]);

  // Mobile: bring the header back when the user forcefully scrolls/pulls up at the top.
  const pageIsAtTop = useCallback(() => window.scrollY <= 4, []);
  usePullToReveal({
    enabled: collapsed && !isDesktop,
    isAtTop: pageIsAtTop,
    onReveal: () => setCollapsed(false),
  });

  // Show the gym logo in the breadcrumb (and make it a "return to top" affordance)
  // whenever the header is out of view — either scrolled away or collapsed on mobile.
  const headerHidden = logoCovered || (collapsed && !isDesktop);

  // Once covered, lead the breadcrumb with the gym logo (reusing the already-loaded
  // image so it paints instantly, no refetch) or a "Top" arrow when there is no logo,
  // making it clear the click returns to the top of the page. The name always stays.
  const gymLogo = gym?.logo_small_url || gym?.logo_url;
  const breadcrumbLeadingIcon = headerHidden ? (
    gymLogo ? (
      <img
        src={gymLogo}
        alt={gym?.name ?? ''}
        className="h-8 w-8 rounded-full object-cover animate-in fade-in zoom-in-95 duration-300"
      />
    ) : (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted animate-in fade-in zoom-in-95 duration-300">
        <ArrowUp className="h-4 w-4" />
      </span>
    )
  ) : undefined;

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: gym
        ? [{
            label: gym.name,
            leadingIcon: breadcrumbLeadingIcon,
            onClick: headerHidden ? handleRevealHeader : undefined,
          }]
        : [],
      hideHomeLogo: headerHidden,
      loading,
    }),
    [gym?.name, gymLogo, headerHidden, handleRevealHeader, loading]
  );

  if (!slug) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <>
        {/* Full-bleed hero placeholder — spans the full viewport width */}
        <div className="-mt-3 -mx-4 sm:-mx-6">
          <Skeleton className="w-full aspect-[16/6]" />
        </div>
        <div className="max-w-4xl mx-auto pt-4 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-10 w-32" />
        </div>
      </>
    );
  }

  if (!gym) {
    return (
      <div className="max-w-4xl mx-auto space-y-1">
        <Card>
          <CardContent className="pt-6 text-center">
            <h1 className="text-2xl font-bold text-muted-foreground">{t('gymProfile.notFound')}</h1>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Joyride
        steps={gymTourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        callback={handleGymTourCallback}
        locale={{
          back: t('tour.back'),
          close: t('tour.close'),
          last: t('tour.last'),
          next: t('tour.next'),
          skip: t('tour.skip'),
        }}
        styles={{
          options: {
            primaryColor: 'hsl(var(--primary))',
            zIndex: 10000,
          },
        }}
      />

      {/* Full-bleed hero cover: spans the full viewport width (breaking out of the
          centered, max-width page container) and sits flush under the navbar. The
          outer app wrapper uses overflow-x-clip so the 100vw breakout never adds a
          horizontal scrollbar. On mobile it collapses when a climb tab is selected,
          and comes back on a forceful scroll-up or a tap on the gym logo in the breadcrumb. */}
      <CollapsibleHeader collapsed={collapsed} enabled={!isDesktop}>
      <Card className="-mt-3 -mx-4 sm:-mx-6 rounded-none border-0 bg-transparent shadow-none">
        <CardContent className="p-0">
          {/* Cover + Logo + Name + Bio */}
          <LocationHeader
            locationId={gym.id}
            name={gym.name}
            bio={gym.bio}
            coverImageUrl={gym.cover_image_url}
            logoUrl={gym.logo_url}
            logoSmallUrl={gym.logo_small_url}
            galleryImages={gym.gallery_images ?? []}
            fallbackIcon={<span className="text-lg font-bold">{gym.name.charAt(0).toUpperCase()}</span>}
            isStaffMember={!!isStaffMember}
            isStaffAdmin={!!isStaffAdmin}
            hideName
            logoOverlay
            logoRef={logoRef}
            fullBleed
            coverSlideshow
            coverTopRight={
              <>
                {gym.website_url && (
                  <a
                    href={gym.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-md border border-white/20 bg-black/40 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-black/60 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('locationInfo.website')}</span>
                  </a>
                )}
                <Button variant="outline" size="icon" onClick={handleShare} title={t('gymProfile.share')} className={COVER_OVERLAY_BUTTON}>
                  <Share2 className="h-4 w-4" />
                </Button>
                {/* The printable wall poster: something only whoever runs the gym has a use for. */}
                {isStaffMember && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/gyms/${slug}/qr-poster`)}
                    title={t('qrPoster.button')}
                    className={COVER_OVERLAY_BUTTON}
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                )}
                {isStaffMember && !isStaffAdmin && (
                  <Button variant="outline" size="icon" onClick={handleGoToSettings} title={t('gymProfile.editInfo')} className={COVER_OVERLAY_BUTTON}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {isStaffAdmin && (
                  <Button variant="outline" size="icon" onClick={handleGoToSettings} title={t('gymProfile.settings')} className={COVER_OVERLAY_BUTTON}>
                    <Settings className="h-4 w-4" />
                  </Button>
                )}
              </>
            }
            coverBottomLeft={<OpeningStatusBadge hours={gym.hours} locationName={gym.name} />}
            coverBottomRight={
              <>
                <Button
                  data-tour="follow-button"
                  variant="outline"
                  size="icon"
                  onClick={handleHeartClick}
                  disabled={followLoading}
                  title={isFollowing ? t('gymProfile.removeFromFavorites') : t('gymProfile.addToFavorites')}
                  className={COVER_OVERLAY_BUTTON}
                >
                  {followLoading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Heart className={`h-4 w-4 ${isFollowing ? 'fill-red-500 text-red-500' : ''}`} />}
                </Button>
                {isFollowing && slug && (
                  <NotificationBell
                    notifiableType="gym"
                    notifiableSlug={slug}
                    disabled={!user}
                    triggerClassName={COVER_OVERLAY_BUTTON}
                  />
                )}
              </>
            }
            onNameUpdated={newName => setGym(g => g ? { ...g, name: newName } : g)}
            onBioUpdated={newBio => setGym(g => g ? { ...g, bio: newBio } : g)}
            onCoverUpdated={url => setGym(g => g ? { ...g, cover_image_url: url } : g)}
            onLogoUpdated={(url, smallUrl) => setGym(g => g ? { ...g, logo_url: url, logo_small_url: smallUrl } : g)}
            onGalleryUpdated={(imgs: GalleryImage[]) => setGym(g => g ? { ...g, gallery_images: imgs } : g)}
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-2 pt-3 space-y-2">
            {/* Website and opening status are shown as overlays on the cover */}

            {/* Address */}
            <p className="text-muted-foreground text-sm">{gym.full_address}</p>

            {/* Action buttons — share, settings and notifications now live as overlays on the cover image.
                The "add to favorites" CTA stays here, prominent, while unfollowing is done via the cover heart. */}
            {(!isFollowing || (isStaffMember && activeScanId)) && (
              <div className="flex gap-2 items-center flex-wrap">
                {!isFollowing && (
                  <Button
                    variant="default"
                    onClick={followGym}
                    disabled={followLoading || !user}
                    size="sm"
                    className="gap-2"
                  >
                    <Heart className="h-4 w-4" />
                    {t('gymProfile.addToFavorites')}
                  </Button>
                )}

                {isStaffMember && activeScanId && slug && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate(`/gyms/${slug}/scans`)}
                  >
                    <Layers className="h-4 w-4" />
                    {t('climbScan.completeUpload')}
                  </Button>
                )}
              </div>
            )}

            {/* Followers */}
            <FollowersList
              followers={gym.followers || []}
              totalCount={gym.followers_count}
              followable={{ kind: 'gyms', slug: gym.slug }}
            />

            {/* Leaderboard teaser */}
            {slug && <LeaderboardPreview gymSlug={slug} />}

            {/* Gym announcements */}
            {slug && (
              <AnnouncementsCarousel
                gymSlug={slug}
                isStaffMember={!!isStaffMember}
                reloadToken={announcementReloadToken}
              />
            )}
          </div>
        </CardContent>
      </Card>
      </CollapsibleHeader>

      <div className="max-w-4xl mx-auto space-y-2">
        {/* Staff actions + stats collapse together with the header on mobile */}
        {currentUserStaff && (
          <CollapsibleHeader collapsed={collapsed} enabled={!isDesktop}>
            <div className="space-y-1">
              {!isStaffAdmin && (
                <GymStaffActions gymId={gym.id} gymSlug={slug} currentUserStaff={currentUserStaff} />
              )}
              {isStaffMember && <GymStatsCard climbs={staffFilteredClimbs} groupBy={statsGroupBy} />}
            </div>
          </CollapsibleHeader>
        )}

        {/* Climbs list — the section tabs live here and stay visible when the header collapses */}
        <ClimbsList
          locationId={gym.id}
          gymSlug={slug}
          boards={boards}
          newSince={newSince}
          onTabSelect={handleTabSelect}
          onFilteredClimbsChange={isStaffMember ? (climbs) => setStaffFilteredClimbs(climbs) : undefined}
          onGroupByChange={isStaffMember ? (gb) => {
            if (gb !== 'none') setStatsGroupBy(gb);
          } : undefined}
          onAddAnnouncement={isStaffMember ? () => setShowAnnouncementForm(true) : undefined}
        />
      </div>

      {/* Create announcement (staff only) — opened from the climbs FAB */}
      {slug && isStaffMember && (
        <AnnouncementFormDialog
          open={showAnnouncementForm}
          onOpenChange={setShowAnnouncementForm}
          gymSlug={slug}
          onSaved={() => setAnnouncementReloadToken(n => n + 1)}
        />
      )}

      {/* Confirm before unfollowing: leaving means no more updates from the gym */}
      {pushNudge.dialog}

      <AlertDialog open={showUnfollowConfirm} onOpenChange={setShowUnfollowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gymProfile.unfollowConfirmTitle', { name: gym.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('gymProfile.unfollowConfirmDescription', { name: gym.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={followLoading}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={unfollowGym}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('gymProfile.removeFromFavorites')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
